import {
    AlertTriangle,
    ArrowRight,
    CalendarDays,
    CheckCircle2,
    ChevronLeft,
    IndianRupee,
    LoaderCircle,
    ReceiptText,
} from 'lucide-react'
import React, { useContext, useMemo, useState } from 'react'
import { FaInfoCircle } from 'react-icons/fa'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './AdvanceApprovalScreen.module.css'

const parseActiveStore = (rawStore) => {
    try {
        return rawStore && rawStore.trim() !== '' ? JSON.parse(rawStore) : null
    } catch {
        return null
    }
}

const toNumber = (value, fallback = 0) => {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : fallback
}

const formatINR = (amount) => {
    const value = toNumber(amount, 0)
    return value.toLocaleString('en-IN', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    })
}

const todayISO = () => new Date().toISOString().split('T')[0]

const AdvanceApprovalScreen = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const { advanceIdentifier } = useParams()
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)

    const advance = location?.state?.advance || null
    const requestedAmount = toNumber(advance?.requestedAmount, 0)

    const [approvedAmount, setApprovedAmount] = useState(
        requestedAmount > 0 ? requestedAmount.toString() : ''
    )
    const [disbursedOn, setDisbursedOn] = useState(todayISO())
    const [isApproving, setIsApproving] = useState(false)
    const [errors, setErrors] = useState({})

    const approvedAmountNumber = useMemo(() => toNumber(approvedAmount, 0), [approvedAmount])
    const isPartialApproval =
        requestedAmount > 0 && approvedAmountNumber > 0 && approvedAmountNumber < requestedAmount

    const partialApprovalMonths = useMemo(() => {
        const emiAmount = toNumber(advance?.emiAmount, 0)
        if (!isPartialApproval || String(advance?.repaymentType || '') !== 'FIXED_EMI') return null
        if (emiAmount <= 0) return null
        return Math.ceil(approvedAmountNumber / emiAmount)
    }, [advance?.emiAmount, advance?.repaymentType, approvedAmountNumber, isPartialApproval])

    const repaymentLabel = useMemo(() => {
        const repaymentType = String(advance?.repaymentType || '')
        if (repaymentType === 'FIXED_EMI') {
            return `FIXED_EMI — ₹${formatINR(advance?.emiAmount)}/month`
        }
        if (repaymentType === 'PERCENT_OF_NET') {
            return `PERCENT_OF_NET — ${toNumber(advance?.emiPercent, 0).toFixed(1)}%`
        }
        return '—'
    }, [advance?.emiAmount, advance?.emiPercent, advance?.repaymentType])

    const approveAdvance = async () => {
        if (!authCtx?.token || !activeStore?.storeIdentifier || !advanceIdentifier) return

        const newErrors = {}
        const amount = parseFloat(approvedAmount)

        if (!amount || amount <= 0) {
            newErrors.approvedAmount = 'Enter a valid amount.'
        }

        if (requestedAmount > 0 && amount > requestedAmount) {
            newErrors.approvedAmount = `Cannot exceed requested amount of ₹${formatINR(requestedAmount)}.`
        }

        if (!disbursedOn) {
            newErrors.disbursedOn = 'Select a disbursement date.'
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors)
            return
        }

        setErrors({})

        try {
            setIsApproving(true)

            const response = await fetch(BASE_URL_API + 'approveStaffAdvanceRequest', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier: activeStore.storeIdentifier,
                    advanceIdentifier,
                    approvedBy: authCtx?.authIdentifier || authCtx?.staffIdentifier || '',
                    approvedAmount: amount,
                    disbursedOn,
                }),
            })

            const respo = await response.json()

            if (respo?.status === 'success') {
                const {
                    approvedAmount: amountApproved,
                    firstDeductionCycle,
                    scheduleRecalculated,
                } = respo.response || {}

                let msg = `Advance of ₹${formatINR(amountApproved)} approved.`
                if (firstDeductionCycle) msg += ` First EMI in ${firstDeductionCycle}.`
                if (scheduleRecalculated) msg += ' Schedule recalculated for partial approval.'

                toast.success(msg)
                navigate(-1)
                return
            }

            if (respo?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            switch (respo?.errorCode) {
                case 'ADV_001':
                    toast.error('Advance not found.')
                    navigate(-1)
                    break
                case 'ADV_002':
                    toast.error(
                        `Cannot approve — advance is already "${respo?.response?.currentStatus || 'updated'}".`
                    )
                    navigate(-1)
                    break
                case 'VAL_001':
                    toast.error(respo?.message || 'Approver identity missing.')
                    break
                case 'VAL_002':
                    setErrors((prev) => ({ ...prev, approvedAmount: respo?.message || 'Invalid amount.' }))
                    break
                case 'VAL_005':
                    setErrors((prev) => ({ ...prev, disbursedOn: 'Invalid date.' }))
                    break
                default:
                    toast.error(respo?.message || 'Approval failed.')
            }
        } catch (error) {
            console.error('approveAdvance error:', error)
            toast.error('Network error.')
        } finally {
            setIsApproving(false)
        }
    }

    if (!advanceIdentifier) {
        return (
            <div className={styles.page}>
                <div className={styles.alertCard}>
                    <AlertTriangle size={16} />
                    <div>
                        <strong>No advance selected.</strong>
                        <p>Open this screen from an active advance card.</p>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className={styles.page}>
            <div className={styles.header}>
                <button
                    className={styles.backBtn}
                    type="button"
                    onClick={() => navigate(-1)}
                    disabled={isApproving}
                >
                    <ChevronLeft size={18} />
                </button>
                <div className={styles.headerMeta}>
                    <span className={styles.heroPill}>
                        <CheckCircle2 size={12} />
                        Owner Action
                    </span>
                    <h1 className={styles.headerTitle}>Approve Advance Request</h1>
                </div>
            </div>

            <div className={styles.summaryCard}>
                <div className={styles.summaryHead}>
                    <ReceiptText size={14} />
                    <span>Advance Summary</span>
                </div>
                <div className={styles.summaryGrid}>
                    <p>
                        <span>Staff</span>
                        <strong>{advance?.staffName || 'Staff'}</strong>
                    </p>
                    <p>
                        <span>Requested</span>
                        <strong>₹{formatINR(requestedAmount)}</strong>
                    </p>
                    <p>
                        <span>Repayment</span>
                        <strong>{repaymentLabel}</strong>
                    </p>
                    <p>
                        <span>Reason</span>
                        <strong>{advance?.requestRemarks || advance?.notes || '—'}</strong>
                    </p>
                </div>
            </div>

            <div className={styles.formCard}>
                <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel}>
                        <IndianRupee size={13} /> Approved Amount (₹) *
                    </label>
                    <input
                        type="number"
                        className={`${styles.textInput} ${errors?.approvedAmount ? styles.inputError : ''}`}
                        value={approvedAmount}
                        onChange={(event) => {
                            setApprovedAmount(event.target.value)
                            setErrors((prev) => ({ ...prev, approvedAmount: undefined }))
                        }}
                        min="0"
                    />
                    <span className={styles.fieldHint}>Requested: ₹{formatINR(requestedAmount)}</span>
                    {errors?.approvedAmount ? (
                        <span className={styles.errorText}>{errors.approvedAmount}</span>
                    ) : null}
                </div>

                {isPartialApproval ? (
                    <div className={styles.partialNote}>
                        <FaInfoCircle size={13} className={styles.partialIcon} />
                        <span>
                            Partial approval: schedule will be recalculated for ₹{formatINR(approvedAmountNumber)}
                            {partialApprovalMonths ? ` over ${partialApprovalMonths} months.` : '.'}
                        </span>
                    </div>
                ) : null}

                <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel}>
                        <CalendarDays size={13} /> Disbursement Date *
                    </label>
                    <input
                        type="date"
                        className={`${styles.textInput} ${errors?.disbursedOn ? styles.inputError : ''}`}
                        value={disbursedOn}
                        onChange={(event) => {
                            setDisbursedOn(event.target.value)
                            setErrors((prev) => ({ ...prev, disbursedOn: undefined }))
                        }}
                    />
                    {errors?.disbursedOn ? (
                        <span className={styles.errorText}>{errors.disbursedOn}</span>
                    ) : null}
                </div>

                <button
                    type="button"
                    className={styles.approveBtn}
                    onClick={approveAdvance}
                    disabled={isApproving}
                >
                    {isApproving ? (
                        <>
                            <LoaderCircle size={15} className={styles.spinIcon} /> Approving...
                        </>
                    ) : (
                        <>
                            Approve &amp; Activate <ArrowRight size={14} />
                        </>
                    )}
                </button>
            </div>
        </div>
    )
}

export default AdvanceApprovalScreen